import { css } from "@repo/styled-system/css";
import type { MetricKind, TrendPointResponse } from "./analyticsApi";
import { isRatioMetric } from "./formatMetric";

/**
 * A tiny dependency-free bar sparkline for one venue's per-period trend (tasks/cross-venue-analytics.md).
 * `NO_DATA`/`NOT_ENABLED` periods render as an explicit GAP (no bar) so a sparse newly-onboarded venue
 * reads as "no history", never as a run of zeros (edge cases 4/6). An `inProgress` period is drawn
 * lighter/hatched — its figures are partial (R5). Ratio metrics scale to `[0, 1]`; COVERS scales to the
 * series max.
 */
export function TrendSparkline({
  points,
  kind,
}: {
  points: TrendPointResponse[];
  kind: MetricKind;
}) {
  const values = points.map((p) =>
    p.value.state === "VALUE" && p.value.value !== null ? p.value.value : null,
  );
  const max = isRatioMetric(kind) ? 1 : Math.max(1, ...values.map((v) => v ?? 0));
  const width = 100;
  const height = 28;
  const gap = 2;
  const n = Math.max(points.length, 1);
  const barW = Math.max(1, (width - gap * (n - 1)) / n);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      preserveAspectRatio="none"
      aria-hidden="true"
      className={css({ display: "block", color: "colorPalette.default" })}
    >
      {points.map((p, i) => {
        const v = values[i];
        const x = i * (barW + gap);
        if (v === null || v === undefined) {
          // Gap marker: a faint baseline tick, never a zero-height "0" bar.
          return (
            <line
              key={i}
              x1={x}
              y1={height - 1}
              x2={x + barW}
              y2={height - 1}
              stroke="currentColor"
              strokeOpacity={0.25}
              strokeDasharray="2 2"
            />
          );
        }
        const h = Math.max(1, (Math.min(v, max) / max) * (height - 2));
        return (
          <rect
            key={i}
            x={x}
            y={height - h}
            width={barW}
            height={h}
            rx={1}
            fill="currentColor"
            fillOpacity={p.inProgress ? 0.4 : 0.85}
          />
        );
      })}
    </svg>
  );
}
